import {
  DOCS_COPY,
  DOCS_COPY_FIELDS,
  DOCS_HOME,
  DOCS_HOME_SLUG,
  DOCS_LOCALES,
  DOCS_PAGES,
  getPublicDocsPages,
  getResolvedDocsPage,
  type DocsLocale,
  type DocsPageResolved,
  type DocsUiCopy,
} from '@/lib/docs'
import { hasOverrides, pickOverride } from './contentOverrides'
import type { StaticRouteKeyEntry } from './routeStaticKeysTypes'

type DocsCopyField = (typeof DOCS_COPY_FIELDS)[number]
type DocsField = 'title' | 'description' | `overview.${number}` | `shortcut.${number}` | `related.${string}`

function visibleDocsText(value: string): string {
  return value.replace(/\|\|([^|]+)\|\|/g, '$1')
}

function docsKey(slug: string, field: DocsField): string {
  return `docs.${slug.replace(/\//g, '.')}.${field}`
}

function docsCopyKey(field: DocsCopyField): string {
  return `docs.copy.${field}`
}

function overviewParagraphs(page: DocsPageResolved): string[] {
  return page.overview?.split(/\n\n+/).map(visibleDocsText) ?? []
}

// The docs home serves the Welcome article, so its strings are editable there.
function docsSlugFromRoute(route: string): string | null {
  if (route === DOCS_HOME) return DOCS_HOME_SLUG
  if (route.startsWith(`${DOCS_HOME}/`)) return route.slice(DOCS_HOME.length + 1)
  return null
}

function addPageMessages(out: Record<string, string>, page: DocsPageResolved) {
  out[docsKey(page.slug, 'title')] = visibleDocsText(page.title)
  out[docsKey(page.slug, 'description')] = visibleDocsText(page.description)

  overviewParagraphs(page).forEach((paragraph, index) => {
    out[docsKey(page.slug, `overview.${index}`)] = paragraph
  })

  page.shortcuts.forEach((shortcut, index) => {
    out[docsKey(page.slug, `shortcut.${index}`)] = visibleDocsText(shortcut.label)
  })
}

export function getDocsMessageCatalog(locale: DocsLocale): Record<string, string> {
  const out: Record<string, string> = {}
  for (const field of DOCS_COPY_FIELDS) {
    out[docsCopyKey(field)] = DOCS_COPY[locale][field]
  }
  for (const sourcePage of DOCS_PAGES) {
    const page = getResolvedDocsPage(sourcePage.slug, locale)
    if (page) addPageMessages(out, page)
  }
  return out
}

export function getDocsRouteStaticEntries(route: string, locale: string): StaticRouteKeyEntry[] {
  const slug = docsSlugFromRoute(route)
  if (!slug) return []

  // MCP docs (`/docs/mcp*`) render `McpConnectorDocs`, whose
  // copy comes from next-intl extract hashes — NOT this table-driven catalog. So
  // table `docs.mcp.*` entries (overview/shortcuts/description) would save but
  // never change the visible page. Don't list them: the MCP copy is editable via
  // its own next-intl hashes, and the breadcrumb title stays click-editable via
  // the bridge's visible-hash path (the catalog still carries `docs.mcp.*`).
  if (slug === 'mcp' || slug.startsWith('mcp/')) return []

  const enPage = getResolvedDocsPage(slug, 'en')
  if (!enPage) return []
  const docsLocale = (DOCS_LOCALES as readonly string[]).includes(locale) ? (locale as DocsLocale) : 'en'
  const localePage = getResolvedDocsPage(slug, docsLocale) ?? enPage
  const enCopy = DOCS_COPY.en
  const localeCopy = DOCS_COPY[docsLocale]

  const entries: StaticRouteKeyEntry[] = [
    {
      key: docsKey(slug, 'title'),
      source: visibleDocsText(enPage.title),
      localeValue: visibleDocsText(localePage.title),
      sources: [{ chain: ['docs content', 'title'], file: 'src/lib/docs/content.ts' }],
    },
    {
      key: docsKey(slug, 'description'),
      source: visibleDocsText(enPage.description),
      localeValue: visibleDocsText(localePage.description),
      sources: [{ chain: ['docs content', 'description'], file: 'src/lib/docs/content.ts' }],
    },
  ]

  function addCopyEntry(field: DocsCopyField, label: string) {
    entries.push({
      key: docsCopyKey(field),
      source: enCopy[field],
      localeValue: localeCopy[field],
      sources: [{ chain: ['docs chrome', label], file: 'src/lib/docs/copy.ts' }],
    })
  }

  if (enPage.overview) addCopyEntry('overview', 'overview')
  if (enPage.videoUrl) addCopyEntry('tutorial', 'tutorial')
  if (enPage.shortcuts.length > 0) addCopyEntry('shortcuts', 'shortcuts')
  if (enPage.glossaryTerms.length > 0) addCopyEntry('glossary', 'glossary')
  if (enPage.relatedPages.length > 0) addCopyEntry('relatedPages', 'related pages')

  const enParagraphs = overviewParagraphs(enPage)
  const localeParagraphs = overviewParagraphs(localePage)
  enParagraphs.forEach((paragraph, index) => {
    entries.push({
      key: docsKey(slug, `overview.${index}`),
      source: paragraph,
      localeValue: localeParagraphs[index],
      sources: [{ chain: ['docs content', `overview ${index + 1}`], file: 'src/lib/docs/content.ts' }],
    })
  })

  enPage.shortcuts.forEach((shortcut, index) => {
    entries.push({
      key: docsKey(slug, `shortcut.${index}`),
      source: visibleDocsText(shortcut.label),
      localeValue: visibleDocsText(localePage.shortcuts[index]?.label ?? ''),
      sources: [{ chain: ['docs shortcuts', shortcut.keys], file: 'src/lib/docs/resolve.ts' }],
    })
  })

  enPage.relatedPages.forEach(related => {
    const localeRelated = localePage.relatedPages.find(item => item.slug === related.slug)
    entries.push({
      key: docsKey(related.slug, 'title'),
      source: visibleDocsText(related.title),
      localeValue: visibleDocsText(localeRelated?.title ?? ''),
      sources: [{ chain: ['docs related pages'], file: 'src/lib/docs/content.ts' }],
    })
  })

  // The same walk the docs route uses for prev/next links (public pages only).
  const allSlugs = getPublicDocsPages(docsLocale).map(page => page.slug)
  const pageIndex = allSlugs.indexOf(slug)
  if (pageIndex > 0) addCopyEntry('previous', 'previous page')
  if (pageIndex >= 0 && pageIndex < allSlugs.length - 1) addCopyEntry('next', 'next page')

  return entries
}

// ---------------------------------------------------------------------------
// Runtime override application
//
// Docs pages render from the hard-coded per-locale content tables, not via
// next-intl `t()`, so saved overrides don't reach them through request.ts.
// These helpers overlay overrides by the SAME keys the catalog above exposes,
// so an edit saved in `/translate` changes the rendered page (gated upstream by
// `contentOverridesEnabled()` — production passes no overrides and renders the
// table verbatim). Override values are markup-free visible text; the table
// fallback keeps its `||kbd||` markers, which `renderWithKbd` still handles.
// ---------------------------------------------------------------------------

function overrideOverview(slug: string, overview: string, overrides: Record<string, string>): string {
  return overview
    .split(/\n\n+/)
    .map((paragraph, index) => pickOverride(overrides, docsKey(slug, `overview.${index}`), paragraph))
    .join('\n\n')
}

export function applyDocsPageOverrides(
  page: DocsPageResolved,
  overrides: Record<string, string> | null | undefined,
): DocsPageResolved {
  if (!hasOverrides(overrides)) return page
  return {
    ...page,
    title: pickOverride(overrides, docsKey(page.slug, 'title'), page.title),
    description: pickOverride(overrides, docsKey(page.slug, 'description'), page.description),
    overview: page.overview ? overrideOverview(page.slug, page.overview, overrides) : page.overview,
    shortcuts: page.shortcuts.map((shortcut, index) => ({
      ...shortcut,
      label: pickOverride(overrides, docsKey(page.slug, `shortcut.${index}`), shortcut.label),
    })),
    relatedPages: page.relatedPages.map(related => ({
      ...related,
      title: pickOverride(overrides, docsKey(related.slug, 'title'), related.title),
    })),
  }
}

export function applyDocsCopyOverrides(
  copy: DocsUiCopy,
  overrides: Record<string, string> | null | undefined,
): DocsUiCopy {
  if (!hasOverrides(overrides)) return copy
  const out = { ...copy }
  for (const field of DOCS_COPY_FIELDS) {
    out[field] = pickOverride(overrides, docsCopyKey(field), copy[field])
  }
  return out
}

/** Overlay a docs page title by slug — for nav lists (prev/next, sidebars). */
export function overrideDocsTitle(
  slug: string,
  title: string,
  overrides: Record<string, string> | null | undefined,
): string {
  if (!hasOverrides(overrides)) return title
  return pickOverride(overrides, docsKey(slug, 'title'), title)
}
